import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ProjectMeetingItem } from '../../shared/meetings';
import { api } from '../lib/api';
import { meetingDuration, meetingShortDate, meetingTime } from '../lib/meetings';
import { Group } from './primitives';

/** Project page's Meetings group (desktop + phone): meetings tagged to this
    project, newest first. Date, title, time and duration only — summary and
    transcript text never leave Meetings. */
export function ProjectMeetings({ projectId, mobile }: { projectId: string; mobile?: boolean }) {
  const [meetings, setMeetings] = useState<ProjectMeetingItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let live = true;
    setFailed(false);
    api<{ meetings: ProjectMeetingItem[] }>(`/api/admin/projects/${projectId}/meetings`)
      .then((d) => { if (live) setMeetings(d.meetings); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [projectId, reload]);
  // Links change from the meeting page; any successful write refetches.
  useEffect(() => {
    const onMutated = () => setReload((v) => v + 1);
    window.addEventListener('ss:mutated', onMutated);
    return () => window.removeEventListener('ss:mutated', onMutated);
  }, []);
  const empty = mobile ? 'pd-empty' : 'empty-note';
  return (
    <Group
      title="Meetings"
      count={meetings ? meetings.length : undefined}
      className={`pj-meetings${mobile ? ' pj-meetings-mobile' : ''}`}
      link={<Link className={`group-link${mobile ? ' pd-glink' : ''}`} to="/meetings">All meetings →</Link>}
    >
      {meetings?.map((m) => (
        <Link key={m.id} className="row pj-mt-line" to={`/meetings/${m.id}`}>
          <span className="row-icon pj-mt-icon" aria-hidden="true">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
              <rect x="1.8" y="4" width="8.6" height="8" rx="1.6" />
              <path d="m10.4 7.2 3.8-2.1v5.8l-3.8-2.1" />
            </svg>
          </span>
          <span className="pj-mt-date">{meetingShortDate(m.recorded_at)}</span>
          <span className="row-title">{m.title}</span>
          <span className="row-spacer" />
          <span className="row-meta dim">
            {meetingTime(m.recorded_at)} · {meetingDuration(m.duration_seconds)}
          </span>
          <span className="pj-mt-open">Open →</span>
        </Link>
      ))}
      {meetings?.length === 0 && (
        <div className={empty}>No meetings linked. Link one from its page in Meetings.</div>
      )}
      {failed && (
        <div className={empty} role="alert">
          Meetings couldn’t be loaded.{' '}
          <button type="button" className="group-link" onClick={() => setReload((v) => v + 1)}>
            Retry
          </button>
        </div>
      )}
    </Group>
  );
}
